import { useState, useMemo, useCallback } from 'react';

/**
 * Custom hook for managing task filters and sorting
 * @param {Array} rawTasks - Unfiltered tasks from API
 * @returns {Object} Filter state, filtered tasks, and filter functions
 */
const useTaskFilters = (rawTasks = []) => {
  // Filter states
  const [filter, setFilter] = useState('all'); // Status filter
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [activityTypeFilter, setActivityTypeFilter] = useState('all');
  const [intangibleCapitalFilter, setIntangibleCapitalFilter] = useState('all');
  const [dueDateFilter, setDueDateFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [gapScoreFilter, setGapScoreFilter] = useState('all');
  const [showNotRelevant, setShowNotRelevant] = useState(false);

  // Server-side filters (for API calls)
  const [filterCategory, setFilterCategory] = useState(''); // For filtering tasks by category
  const [filterQuestionId, setFilterQuestionId] = useState(''); // For filtering tasks by specific question
  const [assessmentTypeFilter, setAssessmentTypeFilter] = useState('all'); // 'all', 'business', 'personal'

  // Sort states
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc'

  /**
   * Helper function to get current server-side filters
   */
  const getCurrentServerFilters = useCallback(() => {
    const serverFilters = {};
    if (filterQuestionId) {
      serverFilters.questionId = filterQuestionId;
    } else if (filterCategory && filterCategory !== 'all') {
      serverFilters.category = filterCategory;
    }
    return serverFilters;
  }, [filterQuestionId, filterCategory]);

  /**
   * Apply client-side filtering and sorting to tasks
   */
  const filteredTasks = useMemo(() => {
    let filtered = [...rawTasks];

    // Filter out "not_relevant" tasks from "all" filter unless checkbox is checked
    if (filter === 'all' && !showNotRelevant) {
      filtered = filtered.filter(task => task.status !== 'not_relevant');
    } else if (filter !== 'all') {
      filtered = filtered.filter(task => task.status === filter);
    }

    // Apply priority filter
    if (priorityFilter !== 'all') {
      filtered = filtered.filter(task => task.priority === priorityFilter);
    }

    // Apply assessment type filter
    if (assessmentTypeFilter !== 'all') {
      filtered = filtered.filter(task => {
        if (!task.question || !task.question.assessment_type) return false;

        const taskAssessmentType = task.question.assessment_type.toLowerCase();

        // For 'personal' filter, match any assessment type that starts with 'personal'
        // (e.g., 'personal financial', 'personal relational', 'personal spiritual')
        if (assessmentTypeFilter === 'personal') {
          return taskAssessmentType.startsWith('personal');
        }

        // For 'business' filter, match both 'business' and 'family enterprise'
        // since Family Enterprise is part of the Business Gap Assessment
        if (assessmentTypeFilter === 'business') {
          return taskAssessmentType === 'business' || taskAssessmentType === 'family enterprise';
        }

        // For other filters, exact match
        return taskAssessmentType === assessmentTypeFilter;
      });
    }

    // Apply activity type filter
    if (activityTypeFilter !== 'all') {
      filtered = filtered.filter(task =>
        task.question && task.question.activity_type === activityTypeFilter
      );
    }

    // Apply intangible capital filter
    if (intangibleCapitalFilter !== 'all') {
      filtered = filtered.filter(task =>
        task.question && task.question.intangible_asset_type === intangibleCapitalFilter
      );
    }

    // Apply due date filter
    if (dueDateFilter !== 'all') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      filtered = filtered.filter(task => {
        if (!task.due_date) {
          return dueDateFilter === 'no_date';
        }

        const dueDate = new Date(task.due_date);
        dueDate.setHours(0, 0, 0, 0);
        const diffTime = dueDate - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        switch (dueDateFilter) {
          case 'overdue':
            return diffDays < 0;
          case 'today':
            return diffDays === 0;
          case 'next_7_days':
            return diffDays >= 0 && diffDays <= 7;
          case 'next_30_days':
            return diffDays >= 0 && diffDays <= 30;
          case 'next_90_days':
            return diffDays >= 0 && diffDays <= 90;
          case 'beyond_90_days':
            return diffDays > 90;
          case 'no_date':
            return false;
          default:
            return true;
        }
      });
    }

    // Apply gap score filter
    if (gapScoreFilter !== 'all') {
      filtered = filtered.filter(task => {
        if (!task.question || task.question.gap_score === undefined || task.question.gap_score === null) return false;

        const score = task.question.gap_score;
        switch (gapScoreFilter) {
          case 'no_gaps':
            return score > 86;
          case 'minor_gaps':
            return score > 72 && score <= 86;
          case 'considerable_gaps':
            return score > 57 && score <= 72;
          case 'critical_gaps':
            return score > 43 && score <= 57;
          case 'very_critical_gaps':
            return score > 28 && score <= 43;
          case 'extremely_critical':
            return score >= 0 && score <= 28;
          default:
            return true;
        }
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case 'title':
          aValue = a.title.toLowerCase();
          bValue = b.title.toLowerCase();
          break;
        case 'priority':
          const priorityOrder = { high: 3, medium: 2, low: 1 };
          aValue = priorityOrder[a.priority] || 0;
          bValue = priorityOrder[b.priority] || 0;
          break;
        case 'status':
          const statusOrder = { not_started: 1, in_progress: 2, under_review: 3, completed: 4, not_relevant: 5 };
          aValue = statusOrder[a.status] || 0;
          bValue = statusOrder[b.status] || 0;
          break;
        case 'due_date':
          aValue = a.due_date ? new Date(a.due_date).getTime() : 0;
          bValue = b.due_date ? new Date(b.due_date).getTime() : 0;
          break;
        case 'gap_score':
          aValue = a.question?.gap_score || 0;
          bValue = b.question?.gap_score || 0;
          break;
        case 'category':
          aValue = a.question?.category_display || '';
          bValue = b.question?.category_display || '';
          break;
        case 'activity_type':
          aValue = a.question?.activity_type || '';
          bValue = b.question?.activity_type || '';
          break;
        case 'intangible_asset_type':
          aValue = a.question?.intangible_asset_type || '';
          bValue = b.question?.intangible_asset_type || '';
          break;
        case 'created_at':
        default:
          aValue = new Date(a.created_at).getTime();
          bValue = new Date(b.created_at).getTime();
          break;
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });

    return filtered;
  }, [
    rawTasks, filter, showNotRelevant, priorityFilter, activityTypeFilter,
    intangibleCapitalFilter, dueDateFilter, categoryFilter, filterQuestionId,
    assessmentTypeFilter, gapScoreFilter, sortBy, sortOrder
  ]);

  /**
   * Reset all filters to default values
   */
  const resetFilters = useCallback(() => {
    setFilter('all');
    setPriorityFilter('all');
    setActivityTypeFilter('all');
    setIntangibleCapitalFilter('all');
    setDueDateFilter('all');
    setCategoryFilter('all');
    setGapScoreFilter('all');
    setFilterCategory('');
    setFilterQuestionId('');
    setAssessmentTypeFilter('all');
    setSortBy('created_at');
    setSortOrder('desc');
  }, []);

  /**
   * Check if any filters are active
   */
  const hasActiveFilters = useMemo(() => {
    return filter !== 'all' ||
           priorityFilter !== 'all' ||
           activityTypeFilter !== 'all' ||
           intangibleCapitalFilter !== 'all' ||
           dueDateFilter !== 'all' ||
           categoryFilter !== 'all' ||
           gapScoreFilter !== 'all' ||
           assessmentTypeFilter !== 'all' ||
           filterCategory !== '' ||
           filterQuestionId !== '';
  }, [filter, priorityFilter, activityTypeFilter, intangibleCapitalFilter, dueDateFilter, categoryFilter, gapScoreFilter, assessmentTypeFilter, filterCategory, filterQuestionId]);

  return {
    // Filter states
    filter,
    priorityFilter,
    activityTypeFilter,
    intangibleCapitalFilter,
    dueDateFilter,
    categoryFilter,
    gapScoreFilter,
    showNotRelevant,
    filterCategory,
    filterQuestionId,
    assessmentTypeFilter,

    // Sort states
    sortBy,
    sortOrder,

    // Computed values
    filteredTasks,
    hasActiveFilters,

    // Filter setters
    setFilter,
    setPriorityFilter,
    setActivityTypeFilter,
    setIntangibleCapitalFilter,
    setDueDateFilter,
    setCategoryFilter,
    setGapScoreFilter,
    setShowNotRelevant,
    setFilterCategory,
    setFilterQuestionId,
    setAssessmentTypeFilter,

    // Sort setters
    setSortBy,
    setSortOrder,

    // Helper functions
    getCurrentServerFilters,
    resetFilters,
  };
};

export default useTaskFilters;
